import React, { useState, useMemo } from "react";
import { projectsList } from "../electronicsKitData";
import ProjectCard from "./ProjectCard";
import { ConductorTesterWidget, MorseCodeWidget } from "./InteractiveWidgets";
import { Zap, Search, Clock } from "lucide-react";

const categories = [
  { id: "all", label: "🌟 All 15 Projects" },
  { id: "Lights", label: "💡 Lights & Glow" },
  { id: "Sound", label: "🔔 Beeps & Codes" },
  { id: "Motion", label: "🏎️ Motors & Fans" },
  { id: "Sensors", label: "🌧️ Secret Detectors" },
  { id: "Games", label: "🎯 Fun Games" },
];

const ProjectsGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSummaryTable, setShowSummaryTable] = useState(false);

  const filteredProjects = useMemo(() => {
    return projectsList.filter((proj) => {
      // Category filtering
      const matchesCategory =
        selectedCategory === "all" ||
        (selectedCategory === "Lights" && (proj.category === "Lights" || proj.category === "Switches")) ||
        proj.category === selectedCategory;

      // Search query filtering
      const query = searchQuery.toLowerCase().trim();
      if (!query) return matchesCategory;

      const matchesSearch =
        proj.title.toLowerCase().includes(query) ||
        proj.tagline.toLowerCase().includes(query) ||
        proj.description.toLowerCase().includes(query) ||
        proj.kitNeeds.some((n) => n.toLowerCase().includes(query)) ||
        proj.homeNeeds.some((n) => n.toLowerCase().includes(query));

      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <section className="section projects-grid-section" id="projects">
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center reveal-triggered fade-up" data-aos="fade-up">
          <div className="section-badge projects-badge">
            <Zap size={16} /> 15 Hands-on Projects
          </div>
          <h2 className="section-title">Junior Maker Lab: 15 Challenges</h2>
          <p className="section-subtitle">
            Start with Project 1 and build your way to the top! Follow each picture and step: the highlighted part is what you connect next.
          </p>
        </div>

        {/* Master Project Summary Table Toggle */}
        <div className="summary-table-toggle-bar">
          <button
            onClick={() => setShowSummaryTable(!showSummaryTable)}
            className="btn-summary-toggle"
          >
            <span>{showSummaryTable ? "Hide" : "View"} 15 Projects Quick Index & Timetable 📋</span>
          </button>
        </div>

        {showSummaryTable && (
          <div className="master-summary-table-card">
            <div className="table-responsive">
              <table className="projects-table">
                <thead>
                  <tr>
                    <th style={{ width: "80px" }}>Project</th>
                    <th>Name</th>
                    <th className="col-time" style={{ width: "110px" }}>Est. Time</th>
                    <th className="col-cat" style={{ width: "140px" }}>Category</th>
                    <th>Focus Learning</th>
                    <th className="col-action" style={{ width: "130px", textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {projectsList.map((p) => (
                    <tr key={p.id}>
                      <td className="font-bold text-blue-600 whitespace-nowrap">
                        #{p.number}
                      </td>
                      <td className="font-semibold text-slate-800">{p.title}</td>
                      <td className="col-time">
                        <span className="table-time-pill">
                          <Clock size={12} className="inline mr-1" />
                          {p.time}
                        </span>
                      </td>
                      <td className="col-cat">
                        <span className="table-cat-pill">{p.category}</span>
                      </td>
                      <td className="text-slate-600 text-sm">{p.tagline}</td>
                      <td className="col-action" style={{ textAlign: "right" }}>
                        <a href={`#project-${p.id}`} className="table-jump-link">
                          Jump to #{p.number} ↓
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filter and Search Bar */}
        <div className="filter-search-container">
          {/* Category Tabs */}
          <div className="category-tabs">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`category-tab-btn ${selectedCategory === cat.id ? "active" : ""}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by part (e.g. buzzer, motor, foil) or project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search projects"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="clear-search-btn">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Projects Count */}
        <div className="projects-count-indicator">
          Showing <strong>{filteredProjects.length}</strong> of 15 projects
          {selectedCategory !== "all" && <span> in "{selectedCategory}"</span>}
          {searchQuery && <span> matching "{searchQuery}"</span>}
        </div>

        {/* 15 Project Cards Grid */}
        <div className="projects-cards-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              defaultOpen={false}
            />
          ))}
        </div>

        {/* Special Interactive Bench Sections embedded into the project catalog */}
        <div className="mt-16 space-y-12">
          {/* Project 7 Interactive Station */}
          <MorseCodeWidget />

          {/* Project 11 Interactive Lab */}
          <ConductorTesterWidget />
        </div>
      </div>
    </section>
  );
};

export default ProjectsGrid;